// src/pages/LandingPage.tsx
import { useRef, useState, useEffect, lazy, Suspense, memo } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
  type MotionValue,
  type TargetAndTransition,
  type Transition,
  type Easing,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Heart, ChevronRight, Music, Loader2, Play } from "lucide-react";
import { tiktokPixelService } from "../services/tiktok-pixel.service";

// Lazy load GlobeGrid with explicit chunk name
const GlobeGrid3D = lazy(
  () => import(/* webpackChunkName: "globe-grid" */ "../components/common/GlobeGrid")
);

// Constants
const OCCASIONS = [
  "For yourself.",
  "For family and friends.",
  "For love and appreciation.",
  "For healing and reconnection.",
  "For celebration.",
] as const;

// Desktop animations
const DESKTOP_ANIMATIONS = {
  glowPulse: {
    scale: [1, 1.2, 1],
    opacity: [0.05, 0.1, 0.05],
  } satisfies TargetAndTransition,
  textReveal: {
    duration: 1.2,
    ease: [0.22, 1, 0.36, 1] as Easing,
  } satisfies Transition,
} as const;

const ANIMATION_CONFIG = {
  textRotate: {
    interval: 2500,
    duration: 0.6,
  },
} as const;

// Custom hook for mouse parallax effect (disabled on mobile)
const useMouseParallax = (isMobile: boolean) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothX = useSpring(mouseX, { damping: 20, stiffness: 150 });
  const smoothY = useSpring(mouseY, { damping: 20, stiffness: 150 });

  const transforms = {
    gold: {
      x: useTransform(smoothX, [-0.5, 0.5], ["-15%", "15%"]),
      y: useTransform(smoothY, [-0.5, 0.5], ["-10%", "10%"]),
    },
    rose: {
      x: useTransform(smoothX, [-0.5, 0.5], ["10%", "-10%"]),
      y: useTransform(smoothY, [-0.5, 0.5], ["15%", "-15%"]),
    },
    rotate: {
      x: useTransform(smoothY, [-0.5, 0.5], [10, -10]),
      y: useTransform(smoothX, [-0.5, 0.5], [-12, 12]),
    },
  };

  useEffect(() => {
    if (isMobile) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX / window.innerWidth - 0.5);
      mouseY.set(e.clientY / window.innerHeight - 0.5);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isMobile, mouseX, mouseY]);

  return transforms;
};

// Optimized BackgroundGlows for mobile
const BackgroundGlows = memo(
  ({
    roseX,
    roseY,
    goldX,
    goldY,
    isMobile,
  }: {
    roseX?: MotionValue<string>;
    roseY?: MotionValue<string>;
    goldX?: MotionValue<string>;
    goldY?: MotionValue<string>;
    isMobile: boolean;
  }) => {
    // Mobile version - static gradients, no motion values
    if (isMobile) {
      return (
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[10%] right-[-5%] w-[50vw] h-[50vw] bg-red-600/10 rounded-full blur-[40px]" />
          <div className="absolute top-[-20%] left-[-5%] w-[60vw] h-[60vw] bg-yellow-500/10 rounded-full blur-[40px]" />
          <div className="absolute bottom-[-10%] left-[20%] w-[70vw] h-[70vw] bg-blue-600/10 rounded-full blur-[40px]" />
        </div>
      );
    }

    // Desktop version with animations
    return (
      <div className="absolute inset-0 pointer-events-none z-0">
        <motion.div
          style={{ x: roseX, y: roseY }}
          className="absolute top-[10%] right-[-5%] w-[50vw] h-[50vw] bg-red-600/10 rounded-full blur-[160px]"
        />
        <motion.div
          style={{ x: goldX, y: goldY }}
          className="absolute top-[-20%] left-[-5%] w-[60vw] h-[60vw] bg-yellow-500/10 rounded-full blur-[160px]"
        />
        <motion.div
          animate={DESKTOP_ANIMATIONS.glowPulse}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-[-10%] left-[20%] w-[70vw] h-[70vw] bg-blue-600/10 rounded-full blur-[160px]"
        />
      </div>
    );
  }
);

const RotatingText = memo(({ index }: { index: number }) => (
  <div className="h-10 mt-12 overflow-hidden relative">
    <AnimatePresence mode="wait">
      <motion.div
        key={index}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ duration: ANIMATION_CONFIG.textRotate.duration }}
      >
        <p className="text-[10px] md:text-[12px] font-black uppercase tracking-[0.6em] text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.3)]">
          {OCCASIONS[index]}
        </p>
      </motion.div>
    </AnimatePresence>
  </div>
));

// Optimized CTA Button - appears immediately with no animation delay
const CTAButton = memo(({ onClick, label }: { onClick: () => void; label: string }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;

    setLoading(true);

    try {
      await Promise.resolve(onClick());
    } finally {
      setTimeout(() => setLoading(false), 1200);
    }
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={!loading ? { scale: 1.05 } : {}}
      whileTap={!loading ? { scale: 0.95 } : {}}
      disabled={loading}
      onClick={handleClick}
      className={`group flex items-center gap-4 px-10 py-5 rounded-full
        font-bold uppercase tracking-tighter text-[11px]
        shadow-[0_20px_40px_rgba(0,0,0,0.4)]
        transition-all
        ${loading ? "bg-neutral-200 text-neutral-600 cursor-not-allowed" : "bg-white text-black"}`}
    >
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-3"
          >
            <Loader2 size={16} className="animate-spin" />
            <span>Preparing...</span>
          </motion.div>
        ) : (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-4"
          >
            <Play
              size={16}
              className="fill-black text-white group-hover:scale-110 transition-transform"
            />

            <span>{label}</span>

            <ChevronRight size={16} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
});
const Footer = memo(() => (
  <footer className="absolute bottom-8 w-full px-12 flex justify-between items-end z-30">
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-4 opacity-40">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-[10px] font-black uppercase tracking-[0.4em]">LRNSY</span>
      </div>
    </div>
    <div className="hidden md:flex flex-col items-end gap-1 opacity-20">
      <span className="text-[9px] font-bold uppercase tracking-[0.4em]">
        Intentional Expression
      </span>
      <div className="h-px w-12 bg-white/40 mt-1" />
    </div>
  </footer>
));

// Main component
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Custom hook for mouse effects (disabled on mobile)
  const transforms = useMouseParallax(isMobile);

  // Mobile detection with resize handling
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();

    const handleResize = () => {
      checkMobile();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Rotating text interval
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % OCCASIONS.length);
    }, ANIMATION_CONFIG.textRotate.interval);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    tiktokPixelService.trackViewContent(
      {
        content_id: "landing_page",
        content_type: "product",
        content_name: "LRNSY Landing Page",
      },
      0,
      "KES",
      "viewcontent:landing_page"
    );
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen bg-[#040508] text-white overflow-hidden selection:bg-amber-400 selection:text-black"
    >
      <BackgroundGlows
        roseX={transforms.rose.x}
        roseY={transforms.rose.y}
        goldX={transforms.gold.x}
        goldY={transforms.gold.y}
        isMobile={isMobile}
      />

      {/* Only render GlobeGrid on desktop */}
      {!isMobile && (
        <Suspense fallback={null}>
          <GlobeGrid3D isMobile={isMobile} />
        </Suspense>
      )}

      <main className="relative z-20 flex items-center justify-center min-h-screen">
        <motion.section className="flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.6 : 1.2 }}
          >
            <h1 className="text-5xl md:text-7xl lg:text-[100px] font-medium tracking-tighter uppercase leading-[0.85] drop-shadow-2xl">
              Express{" "}
              <span
                className="text-transparent"
                style={{ WebkitTextStroke: `1px rgba(255,255,255,0.8)` }}
              >
                What Matters
              </span>
              <br />
              <span className="font-serif italic lowercase tracking-tight">More</span>{" "}
              Intentionally.
            </h1>

            <RotatingText index={index} />

            <p className="mt-8 text-sm md:text-base text-white/70 font-medium leading-relaxed max-w-2xl mx-auto">
              LRNSY is a platform for expressing love, care, appreciation, forgiveness, and
              connection through meaningful experiences for yourself and the people who matter.
            </p>
          </motion.div>

          {/* CTA Button appears immediately */}
          <div className="mt-14 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            {/* PRIMARY CTA */}
            <CTAButton label="See what you can create" onClick={() => navigate("/examples")} />

            {/* SECONDARY CTA */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/archetypes")}
              className="group flex items-center gap-4 px-10 py-5 rounded-full
      font-bold uppercase tracking-tighter text-[11px]
      text-white border border-white/20 bg-white/5 backdrop-blur-md
      shadow-[0_20px_40px_rgba(0,0,0,0.35)]
      hover:bg-white/10 hover:border-white/30 transition-all"
            >
              <Heart
                size={16}
                className="fill-white/90 group-hover:scale-110 transition-transform"
              />

              <span>Create your own now</span>

              <ChevronRight
                size={16}
                className="group-hover:translate-x-0.5 transition-transform"
              />
            </motion.button>
          </div>

          <div className="flex items-center gap-2 opacity-30 mt-8">
            <Music size={10} />
            <p className="text-[9px] uppercase tracking-[.4em] font-light">Sound Recommended</p>
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
};

LandingPage.displayName = "LandingPage";

export default memo(LandingPage);
